import { useState } from 'react';
import { Link } from 'react-router';
import type { ChangeRequest } from '../api/types';
import { formatActivityTime, requestKindLabel, requestStatusInfo, viaLabel } from '../format';
import { ActivityFilters, type ActivityFilterValues } from './ActivityFilters';
import { ExpandableRow } from './ExpandableRow';
import { RequestReview } from './RequestReview';
import { Badge, Empty } from './ui';

function HistoryRow({ request }: { request: ChangeRequest }) {
  const status = requestStatusInfo[request.status] ?? { label: request.status, tone: 'muted' as const };
  return (
    <ExpandableRow
      label={`依頼の詳細: ${requestKindLabel[request.kind] ?? request.kind}（${request.id}）`}
      className={['expired', 'rejected'].includes(request.status) ? 'activity-subdued' : ''}
      columns={[
        <Badge tone={status.tone}>{status.label}</Badge>,
        <span>{requestKindLabel[request.kind] ?? request.kind}</span>,
        request.serverId ? (
          <Link to={`/servers/${encodeURIComponent(request.serverId)}`}>{request.serverId}</Link>
        ) : (
          '—'
        ),
        <span className="small">
          {request.requestedBy}{' '}
          <span className="tag">{request.autoExecuted ? 'AI が実行' : viaLabel[request.via]}</span>
        </span>,
        <time className="small activity-nowrap" dateTime={request.createdAt}>
          {formatActivityTime(request.createdAt)}
        </time>,
        <span className="small">{request.decidedBy ?? '—'}</span>,
      ]}
    >
      <RequestReview request={request} onDecided={() => undefined} compact expandPlan />
    </ExpandableRow>
  );
}

export function RequestHistory({ requests }: { requests: ChangeRequest[] }) {
  const [filters, setFilters] = useState<ActivityFilterValues>({ server: '', status: '', kind: '' });
  const [limit, setLimit] = useState(50);
  const filtered = requests.filter(
    (request) =>
      (!filters.server || request.serverId === filters.server) &&
      (!filters.status || request.status === filters.status) &&
      (!filters.kind || request.kind === filters.kind),
  );
  return (
    <div className="card stack activity-list">
      <ActivityFilters
        values={filters}
        onChange={(values) => {
          setFilters(values);
          setLimit(50);
        }}
        servers={[...new Set(requests.flatMap((request) => (request.serverId ? [request.serverId] : [])))].sort()}
        kinds={[...new Set(requests.map((request) => request.kind))]
          .sort()
          .map((kind) => ({ value: kind, label: requestKindLabel[kind] ?? kind }))}
        statuses={Object.entries(requestStatusInfo)
          .filter(([status]) => status !== 'pending')
          .map(([value, info]) => ({ value, label: info.label }))}
      />
      {filtered.length === 0 ? (
        <Empty>条件に合う履歴はありません。</Empty>
      ) : (
        <div className="table-wrap">
          <table className="activity-table">
            <thead>
              <tr>
                <th aria-label="詳細" />
                <th>状態</th>
                <th>種類</th>
                <th>サーバー</th>
                <th>依頼者</th>
                <th>日時</th>
                <th>決めた人</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, limit).map((request) => (
                <HistoryRow key={request.id} request={request} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="row activity-pagination">
        <span className="small faint">
          {Math.min(limit, filtered.length)} / {filtered.length} 件
        </span>
        {filtered.length > limit && (
          <button className="btn" onClick={() => setLimit(limit + 50)}>
            もっと見る
          </button>
        )}
      </div>
    </div>
  );
}
